/**
 * InboxStatsInline — the unified inbox's response metrics, on the title row.
 *
 * The prototype puts five metrics inline to the RIGHT of the inbox name, not in
 * a band of their own below it. That placement is the point: the numbers read
 * as a description of the inbox you just named, and a full-width band pushed the
 * list itself below the fold on a laptop.
 *
 * ## Five, not seven
 *
 * `mailboxes` and `subscriptions` used to sit here too. Neither is a response
 * metric — one is configuration and the other is plumbing health — and neither
 * appears in the design. Both now live on the Manage Inboxes page, which is
 * where you go to act on them; a subscription gap shows there as a banner with
 * a Re-verify control, rather than as a number nobody can do anything about
 * from the list.
 *
 * ## `null` and `0` are different answers
 *
 * `unread` and `median_first_reply_seconds` are nullable: null means "not
 * measurable on this inbox" and renders an em dash. Rendering `0` there would
 * claim "nothing is unread" or "we reply instantly" — assertions the data does
 * not support. Every metric counts LOGICAL entries across the WHOLE inbox, not
 * the loaded page: one lead emailed to three reps is one thing to answer, and a
 * per-copy count would report three and make a healthy inbox look like a backlog.
 */
import React from 'react';

/** `2h 14m`, `3m`, or an em dash when there is nothing to report. */
export function formatDuration(seconds) {
    if (seconds == null) return '—';
    const total = Math.max(0, Math.round(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h === 0 && m === 0) return '<1m';
    if (h === 0) return `${m}m`;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

const TONES = {
    default: 'text-stone-900',
    warn: 'text-amber-700',
    good: 'text-emerald-700',
    accent: 'text-[#4D11F6]',
};

/** Label above value, both small — the design's proportions on one line. */
function Stat({ label, value, tone = 'default', testId }) {
    return (
        <span className="flex items-baseline gap-1.5 flex-shrink-0" data-testid={testId}>
            <span className="text-[11px] text-[#A29B95] whitespace-nowrap">{label}</span>
            <span
                className={`text-[13px] font-semibold ${TONES[tone]}`}
                style={{ fontVariantNumeric: 'tabular-nums' }}
            >
                {value}
            </span>
        </span>
    );
}

export default function InboxStatsInline({ stats }) {
    if (!stats) {
        return (
            <div
                className="flex items-center gap-4"
                data-testid="inbox-stats-skeleton"
                aria-busy="true"
            >
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="w-20 h-4 bg-stone-100 rounded animate-pulse" />
                ))}
            </div>
        );
    }

    return (
        <div
            className="flex items-center gap-4 min-w-0 overflow-x-auto"
            data-testid="inbox-stats-row"
        >
            <Stat label="Messages" value={stats.total ?? 0} testId="stat-total" />
            <Stat
                label="Unread"
                value={stats.unread ?? '—'}
                tone="accent"
                testId="stat-unread"
            />
            <Stat
                label="Awaiting reply"
                value={stats.unreplied ?? 0}
                tone={stats.unreplied > 0 ? 'warn' : 'good'}
                testId="stat-unreplied"
            />
            <Stat
                label="Median first reply"
                value={formatDuration(stats.median_first_reply_seconds)}
                testId="stat-median-first-reply"
            />
            <Stat
                label="Replied today"
                value={stats.replied_today ?? 0}
                tone="good"
                testId="stat-replied-today"
            />
        </div>
    );
}
